import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Anchor, AlertTriangle, Container, Wind, Activity, ShieldCheck, Check, Ban, Clock, Stamp, RotateCcw, Download, ChevronRight, FileSignature, Sparkles, Sliders, X, Eye, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ShareSnapshot from "@/components/ShareSnapshot";
import LiveKatlas from "@/components/LiveKatlas";

const ROLE_TONE = {
  port_ops: "border-[#1E40AF]",
  vessel_ops: "border-[#0F766E]",
  terminal_yard: "border-[#B45309]",
  haulage: "border-[#374151]",
  compliance: "border-[#B91C1C]",
  escalation: "border-[#7C3AED]",
};

const STATUS_META = {
  ALLOWED:   { chip: "fc-chip-allowed",   icon: Check,  label: "Allowed" },
  BLOCKED:   { chip: "fc-chip-blocked",   icon: Ban,    label: "Blocked" },
  ESCALATED: { chip: "fc-chip-escalated", icon: Clock,  label: "Escalated" },
  APPROVED:  { chip: "fc-chip-approved",  icon: Stamp,  label: "Approved" },
};

export default function Sandbox() {
  const [data, setData] = useState(null);
  const [activeRole, setActiveRole] = useState("terminal_yard");
  const [receipts, setReceipts] = useState([]);
  const [outcome, setOutcome] = useState(null);   // last KATLAS evaluation
  const [showSifty, setShowSifty] = useState(true);
  const [showRWI, setShowRWI] = useState(false);
  const [sifty, setSifty] = useState(null);
  const [rwiWeights, setRwiWeights] = useState({ weather: 70, congestion: 65, customs: 55, labour: 30 });
  const [rwiRuns, setRwiRuns] = useState(null);

  useEffect(() => {
    (async () => {
      const s = await api.scenario();
      setData(s);
      const ev = await api.evidence();
      setReceipts(ev.receipts || []);
      const sf = await api.sift();
      setSifty(sf);
    })();
  }, []);

  const refreshEvidence = async () => {
    const ev = await api.evidence();
    setReceipts(ev.receipts || []);
  };

  const runEvaluate = async (action) => {
    const res = await api.evaluate(action.id, activeRole, null);
    setOutcome({ ...res, action });
    await refreshEvidence();
    const meta = STATUS_META[res.outcome];
    toast(`KATLAS · ${meta?.label || res.outcome}`, { description: action.label });
  };

  const approveEscalated = async (receipt_id) => {
    await api.approve(receipt_id, "Co-signed under named compliance authority following review.");
    await refreshEvidence();
    toast.success("Escalated action approved · co-signed by Compliance Officer");
    setOutcome(o => o && { ...o, outcome: "APPROVED" });
  };

  const resetAll = async () => {
    await api.resetScenario();
    const s = await api.scenario();
    setData(s);
    setReceipts([]);
    setOutcome(null);
    toast.success("Sandbox reset");
  };

  const onExport = async () => {
    const json = await api.exportEvidence();
    const blob = new Blob([JSON.stringify(json, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `freightcierge-evidence-${Date.now()}.json`;
    a.click(); URL.revokeObjectURL(url);
    toast.success("Evidence pack exported");
  };

  const runRWI = async () => {
    const res = await api.rwi(rwiWeights);
    setRwiRuns(res);
    const sf = await api.sift();
    setSifty(sf);
    toast.success("RWI scenario applied");
  };

  if (!data) return <div className="max-w-[1400px] mx-auto px-6 py-12 text-[var(--ink-soft)]">Loading scenario…</div>;

  const scenario = data.scenario;
  const roles = data.roles;
  const role = roles.find(r => r.id === activeRole);

  return (
    <div className="max-w-[1480px] mx-auto px-6 py-8" data-testid="sandbox-root">
      {/* ============================================================
          PRIMARY · Live KATLAS-signed FreightCierge action
         ============================================================ */}
      <LiveKatlas />

      {/* Yellow-tape divider between live and simulated sections */}
      <div className="fc-tape my-10" aria-hidden />

      {/* ============================================================
          SECONDARY · Simulated demo recap (NOT KATLAS-signed)
          rec-05 · Hard-contained inside a collapsed-by-default block.
         ============================================================ */}
      <details className="bg-[#F8F9FA] border border-[var(--line)] rounded-sm" data-testid="simulated-recap-container">
        <summary
          className="cursor-pointer list-none px-5 py-4 flex items-start gap-3 border-b border-transparent open:border-[var(--line)]"
          data-testid="simulated-recap-banner"
        >
          <AlertTriangle size={16} className="text-[var(--amber)] mt-0.5 shrink-0" strokeWidth={2}/>
          <div className="flex-1">
            <div className="display text-[14px] text-[#7C5E10]">Historical context · simulated demo recap — not KATLAS-signed</div>
            <p className="text-[12px] text-[#7C5E10] mt-1 max-w-[80ch]">
              The panels below walk through the original simulated 6-action governance recap (Allowed · Blocked · Escalated).
              They do not produce KATLAS-signed receipts. The signed receipt above is the live proof. Click to expand.
            </p>
          </div>
          <span className="mono text-[10px] text-[#7C5E10] uppercase tracking-[0.16em] mt-0.5">Expand ▾</span>
        </summary>

        <div className="p-5">
          {/* Top scenario header */}
          <div className="fc-card p-5 mb-5" data-testid="scenario-header">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 grid place-items-center bg-[var(--navy)] text-white rounded-sm">
              <Anchor size={18}/>
            </div>
            <div>
              <div className="fc-eyebrow mb-1">Scenario · {scenario.id} · simulated</div>
              <h1 className="display text-[24px] leading-tight">{scenario.title}</h1>
              <p className="text-[13px] text-[var(--ink-soft)] mt-1">
                Corridor: {scenario.corridor} · Vessel: <strong>{scenario.vessel.name}</strong> · Voyage {scenario.vessel.voyage}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={resetAll} className="rounded-sm h-9" data-testid="reset-sandbox-btn">
              <RotateCcw size={13} className="mr-2"/> Reset sandbox
            </Button>
            <Button variant="outline" onClick={() => window.open("/evidence/print", "_blank")} className="rounded-sm h-9" data-testid="download-pdf-btn">
              <Printer size={13} className="mr-2"/> Download PDF
            </Button>
            <ShareSnapshot enabledSifty={showSifty} enabledRWI={showRWI} />
            <Button onClick={onExport} className="bg-[var(--navy)] hover:bg-black rounded-sm h-9" data-testid="export-from-sandbox-btn">
              <Download size={13} className="mr-2"/> Export JSON
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-5 pt-5 border-t border-[var(--line)]">
          <Stat icon={Wind} label="Weather" value={scenario.vessel.weather}/>
          <Stat icon={AlertTriangle} label="ETA delay" value={`+${scenario.vessel.delay_hours}h`} valueRed/>
          <Stat icon={Container} label="Yard utilisation" value={`${scenario.terminal.yard_capacity_pct}%`} valueRed/>
          <Stat icon={Activity} label="At-risk containers" value={`${scenario.containers.filter(c => c.risk > 0.6).length} of ${scenario.containers.length}`}/>
        </div>
      </div>

      {/* Optional panels toggles */}
      <div className="flex flex-wrap items-center gap-6 mb-5" data-testid="optional-toggles">
        <div className="flex items-center gap-2">
          <Switch checked={showSifty} onCheckedChange={setShowSifty} id="t-sifty" data-testid="toggle-sifty"/>
          <Label htmlFor="t-sifty" className="cursor-pointer text-sm flex items-center gap-1.5">
            <Sparkles size={14} className="text-[var(--teal)]"/> SiftyML predictive signals
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={showRWI} onCheckedChange={setShowRWI} id="t-rwi" data-testid="toggle-rwi"/>
          <Label htmlFor="t-rwi" className="cursor-pointer text-sm flex items-center gap-1.5">
            <Sliders size={14} className="text-[var(--amber)]"/> RWI scenario controls
          </Label>
        </div>
        <div className="ml-auto text-[12px] text-[var(--ink-soft)]">
          Sifty recommends · KATLAS governs · Receipts evidence
        </div>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {/* Left column: timeline + containers */}
        <div className="col-span-12 lg:col-span-4 space-y-5">
          <div className="fc-card" data-testid="event-timeline">
            <SectionHead icon={Activity} title="Event timeline" sub="Signal layer · existing systems"/>
            <ol className="px-5 pb-5">
              {scenario.events.map((e, i) => (
                <li key={i} className="grid grid-cols-[60px_12px_1fr] gap-3 py-3 border-t border-[var(--line)] first:border-t-0">
                  <span className="mono text-[11px] text-[var(--ink-soft)] pt-1">{e.t}</span>
                  <span className="relative flex justify-center">
                    <span className={`fc-pulse-dot ${i < scenario.events.length - 2 ? "!bg-[var(--ink-soft)] !shadow-none" : ""}`}/>
                  </span>
                  <span className="text-[13px] leading-snug">{e.text}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="fc-card" data-testid="container-cards">
            <SectionHead icon={Container} title="Shipments at risk"/>
            <div className="px-5 pb-5 space-y-3">
              {scenario.containers.map(c => <ContainerCard key={c.id} c={c}/>)}
            </div>
          </div>
        </div>

        {/* Center column: role + actions + KATLAS outcome */}
        <div className="col-span-12 lg:col-span-5 space-y-5">
          <div className="fc-card" data-testid="role-switcher">
            <SectionHead icon={Eye} title="Acting as" sub="Switch role to test decision rights"/>
            <div className="px-5 pb-5">
              <div className="grid grid-cols-2 gap-2">
                {roles.map(r => (
                  <button
                    key={r.id}
                    onClick={() => { setActiveRole(r.id); setOutcome(null); }}
                    data-testid={`role-${r.id}`}
                    className={`text-left p-3 border-l-4 ${ROLE_TONE[r.id]} ${activeRole === r.id ? "bg-[#F4F7FB] border-r border-y border-r-[var(--line)] border-y-[var(--line)]" : "border-r border-y border-r-[var(--line)] border-y-transparent hover:bg-[var(--paper)]"}`}
                  >
                    <div className="display text-[13px] leading-tight">{r.label}</div>
                    <div className="text-[11px] text-[var(--ink-soft)] mt-0.5">{r.org}</div>
                  </button>
                ))}
              </div>
              <div className="mt-3 text-[12px] text-[var(--ink-soft)]"><strong className="text-[var(--ink)]">Scope:</strong> {role.scope}</div>
            </div>
          </div>

          <div className="fc-card" data-testid="action-drawer">
            <SectionHead icon={ChevronRight} title="Candidate actions" sub="Recommended by signal layer · governed by KATLAS"/>
            <div className="px-5 pb-5 space-y-2">
              {scenario.actions.map(a => (
                <div key={a.id} className="border border-[var(--line)] p-3" data-testid={`action-${a.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="display text-[14px] leading-tight">{a.label}</div>
                      <div className="text-[12px] text-[var(--ink-soft)] mt-1">{a.summary}</div>
                      <div className="mono text-[10px] uppercase tracking-wider text-[var(--ink-soft)] mt-2">
                        Recommended by · {a.recommended_by}
                      </div>
                    </div>
                    <Button
                      onClick={() => runEvaluate(a)}
                      className="bg-[var(--navy)] hover:bg-black rounded-sm h-8 text-[12px] shrink-0"
                      data-testid={`run-${a.id}`}
                    >
                      Run via KATLAS
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {outcome && (
            <KatlasOutcome
              outcome={outcome}
              activeRole={role}
              onApprove={() => approveEscalated(outcome.receipt.receipt_id)}
              onClose={() => setOutcome(null)}
            />
          )}
        </div>

        {/* Right column: decision rights + sifty + rwi + receipts */}
        <div className="col-span-12 lg:col-span-3 space-y-5">
          <div className="fc-card" data-testid="decision-rights">
            <SectionHead icon={ShieldCheck} title="Decision rights" sub="Who may decide · CAR matrix"/>
            <div className="px-5 pb-5">
              <ul className="text-[12px] space-y-2">
                {scenario.actions.map(a => {
                  const r = roles.find(x => x.id === a.permitted_role);
                  const meta = STATUS_META[a.outcome];
                  const Icon = meta.icon;
                  return (
                    <li key={a.id} className="border-b border-[var(--line)] pb-2 last:border-0">
                      <div className="leading-tight">{a.label}</div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className={`fc-chip ${meta.chip}`}><Icon size={10}/> {meta.label}</span>
                      </div>
                      <div className="text-[11px] text-[var(--ink-soft)] mt-1">→ {r?.label}</div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {showSifty && sifty && (
            <div className="fc-card" data-testid="sifty-panel">
              <SectionHead icon={Sparkles} title="Predictive signals" sub="SiftyML · supporting input only"/>
              <div className="px-5 pb-5">
                <div className="text-[13px] mb-3 text-[var(--ink)]">{sifty.summary}</div>
                {sifty.scores.map((s, i) => (
                  <div key={i} className="mb-2.5">
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="text-[var(--ink-soft)]">{s.label}</span>
                      <span className="mono">{s.value}</span>
                    </div>
                    <div className="h-1.5 bg-[var(--paper)] border border-[var(--line)]">
                      <div className="h-full bg-[var(--navy)]" style={{ width: `${s.value}%` }}/>
                    </div>
                  </div>
                ))}
                <div className="mt-3 pt-3 border-t border-[var(--line)] text-[11px] text-[var(--ink-soft)]">
                  AI may recommend. It does not determine authority.
                </div>
              </div>
            </div>
          )}

          {showRWI && (
            <div className="fc-card" data-testid="rwi-panel">
              <SectionHead icon={Sliders} title="Scenario controls" sub="RWI · stress this scenario"/>
              <div className="px-5 pb-5 space-y-3">
                {[
                  { k: "weather", l: "Weather severity" },
                  { k: "congestion", l: "Yard congestion" },
                  { k: "customs", l: "Customs delay" },
                  { k: "labour", l: "Labour shortage" },
                ].map(s => (
                  <div key={s.k}>
                    <div className="flex justify-between text-[11px] mb-1.5">
                      <span className="text-[var(--ink-soft)]">{s.l}</span>
                      <span className="mono">{rwiWeights[s.k]}</span>
                    </div>
                    <Slider
                      value={[rwiWeights[s.k]]}
                      max={100} step={1}
                      onValueChange={(v) => setRwiWeights({ ...rwiWeights, [s.k]: v[0] })}
                      data-testid={`rwi-slider-${s.k}`}
                    />
                  </div>
                ))}
                <Button onClick={runRWI} className="bg-[var(--navy)] hover:bg-black rounded-sm w-full h-9 text-[12px]" data-testid="rwi-run-btn">
                  Run comparison: unguided vs governed
                </Button>
                {rwiRuns && (
                  <div className="border border-[var(--line)] p-3 mt-2 text-[11px]" data-testid="rwi-results">
                    <div className="grid grid-cols-3 gap-2 mb-2 mono uppercase text-[10px] tracking-wider text-[var(--ink-soft)]">
                      <div></div><div>Unguided</div><div>Governed</div>
                    </div>
                    {[
                      ["Resolution", `${rwiRuns.unguided.resolution_minutes}m`, `${rwiRuns.governed.resolution_minutes}m`],
                      ["Unauth. acts", rwiRuns.unguided.unauthorised_actions, rwiRuns.governed.unauthorised_actions],
                      ["Confidence",  `${rwiRuns.unguided.decision_confidence}%`, `${rwiRuns.governed.decision_confidence}%`],
                      ["Evidence",    `${rwiRuns.unguided.evidence_completeness}%`, `${rwiRuns.governed.evidence_completeness}%`],
                    ].map(row => (
                      <div key={row[0]} className="grid grid-cols-3 gap-2 py-1 border-t border-[var(--line)]">
                        <div>{row[0]}</div>
                        <div className="mono text-[var(--ink-soft)]">{row[1]}</div>
                        <div className="mono text-[var(--green)]"><strong>{row[2]}</strong></div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="fc-card" data-testid="receipt-drawer">
            <SectionHead icon={FileSignature} title="Receipts" sub={`${receipts.length} in evidence pack`}/>
            <div className="max-h-[280px] overflow-y-auto">
              {receipts.length === 0 && <div className="px-5 pb-5 text-[12px] text-[var(--ink-soft)]">No receipts yet. Run an action.</div>}
              {receipts.slice().reverse().map(r => {
                const meta = STATUS_META[r.status] || STATUS_META.ALLOWED;
                const Icon = meta.icon;
                return (
                  <div key={r.receipt_id} className="px-5 py-3 border-t border-[var(--line)] first:border-0" data-testid={`drawer-receipt-${r.receipt_id}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`fc-chip ${meta.chip}`}><Icon size={10}/> {meta.label}</span>
                      <span className="mono text-[10px] text-[var(--ink-soft)] ml-auto">{r.receipt_id}</span>
                    </div>
                    <div className="text-[12px] leading-snug">{r.action_label}</div>
                    <div className="text-[11px] text-[var(--ink-soft)] mt-0.5">{r.actor_role}</div>
                  </div>
                );
              })}
            </div>
            <div className="px-5 pt-3 pb-4 border-t border-[var(--line)]">
              <Link to="/evidence" className="text-[12px] fc-link-underline text-[var(--navy)]">Open full evidence pack →</Link>
            </div>
          </div>
        </div>
      </div>
        </div>
      </details>
    </div>
  );
}

function SectionHead({ icon: Icon, title, sub }) {
  return (
    <div className="px-5 py-4 border-b border-[var(--line)] flex items-start gap-3">
      <Icon size={16} className="text-[var(--navy)] mt-0.5" strokeWidth={1.7}/>
      <div>
        <div className="display text-[14px] leading-tight">{title}</div>
        {sub && <div className="text-[11px] text-[var(--ink-soft)] mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value, valueRed }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={16} className="text-[var(--ink-soft)] mt-1" strokeWidth={1.6}/>
      <div>
        <div className="fc-eyebrow">{label}</div>
        <div className={`display text-[18px] leading-tight ${valueRed ? "text-[var(--red)]" : ""}`}>{value}</div>
      </div>
    </div>
  );
}

function ContainerCard({ c }) {
  const tone = c.priority === "critical" ? "border-[var(--red)]"
             : c.priority === "high"     ? "border-[var(--amber)]"
             : c.priority === "medium"   ? "border-[var(--ink-soft)]"
             :                              "border-[var(--line)]";
  return (
    <div className={`border-l-4 ${tone} bg-white border-r border-y border-r-[var(--line)] border-y-[var(--line)] p-3`} data-testid={`container-${c.id}`}>
      <div className="flex justify-between items-start gap-2">
        <div className="mono text-[12px] font-semibold">{c.id}</div>
        <span className="mono text-[10px] uppercase tracking-wider text-[var(--ink-soft)]">{c.priority}</span>
      </div>
      <div className="text-[12px] mt-1">{c.commodity}</div>
      <div className="text-[11px] text-[var(--ink-soft)] mt-1">{c.commitment}</div>
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-[var(--line)]">
        <div className="text-[11px]">{c.doc_status}</div>
        <div className="flex items-center gap-1.5 text-[11px] mono">
          <span className="w-12 h-1 bg-[var(--paper)] border border-[var(--line)] inline-block">
            <span className="block h-full bg-[var(--red)]" style={{ width: `${c.risk * 100}%` }}/>
          </span>
          {(c.risk * 100).toFixed(0)}
        </div>
      </div>
    </div>
  );
}

function KatlasOutcome({ outcome, activeRole, onApprove, onClose }) {
  const meta = STATUS_META[outcome.outcome] || STATUS_META.ALLOWED;
  const Icon = meta.icon;
  const r = outcome.receipt;
  const isEscalated = outcome.outcome === "ESCALATED";

  return (
    <div className="fc-card border-l-4 border-l-[var(--navy)] fc-fade-up" data-testid="katlas-outcome">
      <div className="px-5 py-4 border-b border-[var(--line)] flex items-center gap-3">
        <ShieldCheck size={16} className="text-[var(--navy)]" strokeWidth={1.7}/>
        <div className="flex-1">
          <div className="fc-eyebrow">KATLAS evaluation</div>
          <div className="display text-[14px] leading-tight">{outcome.action.label}</div>
        </div>
        <button onClick={onClose} className="text-[var(--ink-soft)] hover:text-[var(--ink)]" data-testid="close-outcome">
          <X size={16}/>
        </button>
      </div>
      <div className="p-5 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className={`fc-chip ${meta.chip}`}><Icon size={12}/> {meta.label}</span>
          <span className="mono text-[11px] text-[var(--ink-soft)]">{r.receipt_id}</span>
        </div>

        <KV label="Actor"        value={`${activeRole.label} · ${activeRole.org}`}/>
        <KV label="Authority"    value={r.authority_used}/>
        <KV label="Decision basis" value={r.decision_basis}/>
        <KV label="Downstream"   value={r.downstream.join(" · ")}/>

        {isEscalated && (
          <div className="border border-[var(--amber)] bg-[#FFFBEB] p-3 mt-2">
            <div className="text-[12px] mb-2">
              This action requires named co-sign from the <strong>Compliance / Release Officer</strong>.
            </div>
            <Button onClick={onApprove} className="bg-[var(--navy)] hover:bg-black rounded-sm h-8 text-[12px]" data-testid="approve-escalated-btn">
              <Stamp size={12} className="mr-2"/> Approve as Compliance Officer
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div className="grid grid-cols-[110px_1fr] gap-3 text-[12px]">
      <div className="fc-eyebrow pt-0.5">{label}</div>
      <div className="leading-snug">{value}</div>
    </div>
  );
}
